import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CreditCard, 
  Smartphone, 
  Link2, 
  CheckCircle, 
  ArrowLeft, 
  Info,
  Diamond,
  User,
  DollarSign,
  Copy,
  Share2,
  Clock,
  AlertTriangle,
  Sparkles,
  Calendar,
  Shield,
  QrCode,
  Search,
  ExternalLink,
  UserCheck,
  UserX
} from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';
import CompletarCadastro from '@/components/CompletarCadastro';
import { useSpinner } from '@/hooks/useSpinner';
import { configuracoesService } from '@/services/configuracoes.service';
import { saleService } from '@/services/sale.service';
import { userService } from '@/services/user.service';
import { telegramLogService } from '@/services/telegram-log.service';
import { franchiseService } from '@/services/franchise.service';
import { diamondsService } from '@/services/diamonds.service';
import { clientService } from '@/services/client.service';
import { getSubdomainFranchise } from '@/utils/get-subdomain';
import { Util } from '@/utils/utils';
import Swal from 'sweetalert2';
import { useToast } from '@/hooks/use-toast';
import { navigateToRoute } from '@/utils/environment-urls';


interface Parcela {
  qntd: number;
  valor: number;
}

interface UserData {
  id: number;
  codigo: string;
  nome_razao_social: string;
  email: string;
  phones: Array<{ telefone: string }>;
  cpf_cnpj: string;
  role: string;
  address?: any;
}

interface ClienteInfo {
  id?: number;
  nome?: string;
  cpf?: string;
  telefone?: string;
  indicacao?: {
    existe: boolean;
    indicadora_id: number | null;
    nivel: number | null;
    data_indicacao: string | null;
  };
  rede?: {
    esta_na_rede: boolean;
    nivel_na_rede: number | null;
    data_indicacao_rede: string | null;
  };
  isSelf?: boolean;
  isIndicatedByCurrentUser?: boolean;
  isIndicatedByOther?: boolean;
  isIndicated?: boolean;
  isIndicatedInLevel2or3?: boolean;
  willBeAdded?: boolean;
}

const NovaVenda = () => {
  const { isLoading, title, message, showSpinner, hideSpinner } = useSpinner();
  const util = new Util();
  const subdomain = getSubdomainFranchise();
  const { toast } = useToast();

  // Estados do formulário
  const [valorTotal, setValorTotal] = useState<number>(0);
  const [nomeCliente, setNomeCliente] = useState('');
  const [cpfCliente, setCpfCliente] = useState('');
  const [clienteInfo, setClienteInfo] = useState<ClienteInfo | null>(null);
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [isSearchingClient, setIsSearchingClient] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const [showIndicationOption, setShowIndicationOption] = useState(false);
  const [typeSale, setTypeSale] = useState<'card' | 'pix' | 'link'>('card');
  const [currentStep, setCurrentStep] = useState(0);

  // Estados para autocomplete de clientes
  const [clientesPreCarregados, setClientesPreCarregados] = useState<Array<{ id: number; nome: string; cpf: string; telefone: string | null }>>([]);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Estados para configurações
  const [maxParcelas, setMaxParcelas] = useState(5);
  const [valMinParcela, setValMinParcela] = useState(50);
  const [linkPgtoAtivoAdmin, setLinkPgtoAtivoAdmin] = useState(false);
  const [diamondsValue, setDiamondsValue] = useState(50);
  const [statusAdesao, setStatusAdesao] = useState<string | null>(null);

  // Estados para parcelas
  const [opcoesParcelas, setOpcoesParcelas] = useState<Parcela[]>([]);
  const [parcelaSelecionada, setParcelaSelecionada] = useState<Parcela>({ qntd: 1, valor: 0 });

  // Estados para PIX
  const [documentPix, setDocumentPix] = useState('');
  const [pixCopiaCola, setPixCopiaCola] = useState('');
  const [qrCodePix, setQrCodePix] = useState('');
  const [pixExpired, setPixExpired] = useState(false);
  const [salePixId, setSalePixId] = useState('');

  // Estados para cartão
  const [numsCard, setNumsCard] = useState('');
  const [expCard, setExpCard] = useState('');
  const [cvvCard, setCvvCard] = useState('');
  const [nameCard, setNameCard] = useState('');

  // Estados para link
  const [linkCopiaCola, setLinkCopiaCola] = useState('');

  // Dados do usuário
  const [userData, setUserData] = useState<UserData | null>(null);
  const [showCompletarCadastro, setShowCompletarCadastro] = useState(false);

  const navigateToLogin = () => {
    window.location.href = '/authentication/side-login';
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownAberto(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const userDataString = localStorage.getItem('userlogged');
    const parsedUserData = JSON.parse(userDataString);
    if(parsedUserData.role == 'cliente'){
      navigateToRoute('/home-cliente', true);
    }
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    // Carregar configurações
    showSpinner('Aguarde', 'Carregando configurações');
    // Verificar se a franquia está suspensa
    try {
      const franquiasDisponiveis = await franchiseService.findAllAvailable();
      const franquiaSelecionadaObj = franquiasDisponiveis.find((f: any) => f.subdomain === subdomain);
      
      if (franquiaSelecionadaObj && franquiaSelecionadaObj.status === 'suspenso') {
        navigateToLogin();
        return;
      }
    } catch (err) {
      console.log(err);
    }

    // Verificar dados do usuário
    const userDataString = localStorage.getItem('userlogged');
    if (!userDataString) {
      await Swal.fire({
        title: 'Ops',
        text: 'Sessão expirada, autentique novamente',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      navigateToLogin();
      return;
    }

    const parsedUserData = JSON.parse(userDataString);
    setUserData(parsedUserData);

    // Verificação de pré-cadastro para subdomain 'matriz'
    if (subdomain === 'matriz' && parsedUserData.cpf_cnpj) {
      try {
        const response = await userService.checkPreRegister(parsedUserData.cpf_cnpj);
        const preRegisterData = response.data;
        
        if (preRegisterData && preRegisterData.situacao && 
            (preRegisterData.situacao === 'aguardando_assinaturas' || 
             preRegisterData.situacao === 'verificando_contrato')) {
          // Redireciona para a página de pré-cadastro no Angular
          navigateToRoute(`/painel/venda/pre-cadastro/${preRegisterData.id}`, false);
          return;
        } else {
          // Se não tem pré-cadastro pendente, verifica se tem endereço
          const token = localStorage.getItem('token');
          if (token && parsedUserData && !parsedUserData.address) {
            setShowCompletarCadastro(true);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar pré-cadastro:', error);
        toast({
          title: "Erro",
          description: "Erro ao verificar dados. Por favor, tente novamente mais tarde.",
          variant: "destructive",
        });
      }
    } else {
      // Para outros subdomains, só verifica se tem endereço
      const token = localStorage.getItem('token');
      if (token && parsedUserData && !parsedUserData.address) {
        setShowCompletarCadastro(true);
      }
    }
    
    try {
      await Promise.all([
        loadConfigurations(),
        getAdesao(parsedUserData.id),
        clientService.getClientsByRevendedora(parsedUserData.id).then(setClientesPreCarregados)
      ]);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      hideSpinner();
    }
  };

  const loadConfigurations = async () => {
    try {
      const configs = await configuracoesService.getAllConfigs();
      
      if (configs.length > 0) {
        const maxParcelasConfig = configs.find((config: any) => config.name === 'max_installments');
        const valMinParcelaConfig = configs.find((config: any) => config.name === 'min_value_installments');
        const linkParamConfig = configs.find((config: any) => config.name === 'link_pagto');
        const diamondsValueConfig = configs.find((config: any) => config.name === 'diamonds_value');

        if (maxParcelasConfig) setMaxParcelas(maxParcelasConfig.value);
        if (valMinParcelaConfig) setValMinParcela(valMinParcelaConfig.value);
        if (linkParamConfig) setLinkPgtoAtivoAdmin(linkParamConfig.value === '1');
        if (diamondsValueConfig) setDiamondsValue(diamondsValueConfig.value);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const getAdesao = async (userId: number) => {
    try {
      const resp = await diamondsService.getAdesao(userId);
      if (resp && resp[0]) {
        setStatusAdesao(resp[0].status);
      } else {
        setStatusAdesao(null);
      }
    } catch (error) {
      console.error('Erro ao verificar adesão:', error);
    }
  };

  const handleCompletarCadastroSuccess = (newUserData: any) => {
    setUserData(newUserData);
    setShowCompletarCadastro(false);
  };

  const formatCurrency = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    const amount = parseInt(numbers || '0') / 100;
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/\D/g, ''); // Remove tudo que não é dígito
    const numericValue = parseInt(inputValue || '0') / 100; // Converte para número com 2 casas decimais
    setValorTotal(numericValue);
  };

  const getDropdownFiltrado = () => {
    const query = buscaCliente.trim();
    if (query.length < 2) return [];
    const queryLower = query.toLowerCase();
    const cpfNums = query.replace(/\D/g, '');
    return clientesPreCarregados.filter(c => {
      const nomeMatch = c.nome.toLowerCase().includes(queryLower);
      const cpfMatch = cpfNums.length >= 3 && c.cpf.replace(/\D/g, '').includes(cpfNums);
      return nomeMatch || cpfMatch;
    }).slice(0, 8);
  };

  const handleBuscaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBuscaCliente(value);

    // Detectar se parece CPF (maioria dígitos)
    const onlyDigitsAndSeps = /^[\d.\-/]*$/.test(value);
    if (onlyDigitsAndSeps) {
      const formatted = formatCPF(value);
      setCpfCliente(formatted);
      setDocumentPix(formatted);
      const numbers = formatted.replace(/\D/g, '');
      if (numbers.length === 11) {
        setDropdownAberto(false);
        searchClient(formatted);
        return;
      } else {
        setClienteInfo(null);
        setClienteId(null);
        setShowNameInput(false);
        setShowIndicationOption(false);
        setNomeCliente('');
      }
    } else {
      // Contém letras — reset CPF pois é busca por nome
      setCpfCliente('');
      setDocumentPix('');
      setClienteInfo(null);
      setClienteId(null);
      setShowNameInput(false);
      setShowIndicationOption(false);
      setNomeCliente('');
    }

    // Abrir dropdown se tiver resultados
    const queryLen = value.trim().length;
    setDropdownAberto(queryLen >= 2);
  };

  const selecionarClienteDropdown = (cliente: { id: number; nome: string; cpf: string; telefone: string | null }) => {
    setBuscaCliente(cliente.nome);
    setCpfCliente(cliente.cpf);
    setDocumentPix(cliente.cpf);
    setDropdownAberto(false);
    searchClient(cliente.cpf);
  };

  const searchClient = async (cpf?: string) => {
    const cpfToSearch = cpf || cpfCliente;
    const numbers = cpfToSearch.replace(/\D/g, '');
    
    if (numbers.length !== 11) {
      toast({
        title: 'CPF inválido',
        description: 'Digite um CPF válido com 11 dígitos',
        variant: 'destructive'
      });
      return;
    }

    if (!userData) return;

    if (cpfToSearch.replace(/\D/g, '') === userData.cpf_cnpj.replace(/\D/g, '')) {
      setClienteInfo({      
        isSelf: true
      });
      setNomeCliente('');
      setShowNameInput(true);
      setShowIndicationOption(false);
      return;
    }

    setIsSearchingClient(true);
    
    try {
      const response = await clientService.getUserClient({
        cpf: numbers,
        id_revendedora: userData.id
      });

      if (response.success && response.cliente) {
        const cliente = response.cliente;
        setDocumentPix(cliente.cpf)
        setClienteId(cliente.id); // Salvar o ID do cliente
        const indicacao = response.indicacao;
        const rede = response.rede;
        
        if (indicacao.existe) {
          // Cliente foi indicado por alguém
          if (indicacao.indicadora_id === userData.id) {
            // Cliente foi indicado pela revendedora atual
            setClienteInfo({
              ...cliente,
              indicacao: indicacao,
              rede: rede,
              isIndicatedByCurrentUser: true,
              isIndicated: true,
              isIndicatedInLevel2or3: false
            });
            setNomeCliente(cliente.nome);
            setShowNameInput(false);
            setShowIndicationOption(false);
          } else {
            // Cliente foi indicado por outra revendedora
            // Verificar se está na rede nos níveis 2 ou 3
            const isInNetwork = rede && rede.esta_na_rede && (rede.nivel_na_rede === 2 || rede.nivel_na_rede === 3);
            
            setClienteInfo({
              ...cliente,
              indicacao: indicacao,
              rede: rede,
              isIndicatedByOther: true,
              isIndicated: true,
              isIndicatedInLevel2or3: isInNetwork
            });
            setNomeCliente(cliente.nome);
            setShowNameInput(false);
            setShowIndicationOption(false);
          }
        } else {
          // Cliente encontrado mas não indicado por ninguém
          // Será adicionado à rede da revendedora atual
          setClienteInfo({
            ...cliente,
            rede: rede,
            willBeAdded: true,
            isIndicated: false,
            isIndicatedInLevel2or3: false
          });
          setNomeCliente(cliente.nome);
          setShowNameInput(false);
          setShowIndicationOption(false);
        }
      } else {
        // Cliente não encontrado
        setClienteInfo(null);
        setClienteId(null); // Reset do ID quando não encontrado
        setShowNameInput(true);
        setShowIndicationOption(true);
        setNomeCliente('');
      }
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      toast({
        title: 'Erro na busca',
        description: 'Erro ao buscar cliente. Tente novamente.',
        variant: 'destructive'
      });
      setClienteInfo(null);
      setClienteId(null); // Reset do ID em caso de erro
      setShowNameInput(true);
      setShowIndicationOption(false);
    } finally {
      setIsSearchingClient(false);
    }
  };

  const generateIndicationLink = () => {
    if (!userData) return '';
    
    const baseUrl = window.location.origin;
    return `${baseUrl}/v2/cadastro-cliente/${userData.codigo}`;
  };

  const copyIndicationLink = async () => {
    const link = generateIndicationLink();
    try {
      await navigator.clipboard.writeText(link);
      toast({
        title: 'Link copiado!',       
        variant: 'default',
        duration: 1500
      });
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o link. Tente novamente.',
        variant: 'destructive',
        duration: 5000
      });
    }
  };

  const shareIndicationLink = async () => {
    const link = generateIndicationLink();
    const text = `Olá! Cadastre-se como cliente através do meu link de indicação: ${link}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Link de Indicação - X do Infinito',
          text: text,
          url: link
        });
      } catch (error) {
        console.error('Erro ao compartilhar:', error);
      }
    } else {
      // Fallback para copiar
      copyIndicationLink();
    }
  };

  const calculaParcelas = () => {
    const parcelas: Parcela[] = [];
    parcelas.push({ qntd: 1, valor: valorTotal });
    
    let parc = 2;
    while (parc <= maxParcelas) {
      let valDividido = valorTotal / parc;
      valDividido = parseFloat(valDividido.toFixed(2));
      
      if (valDividido < valMinParcela) {
        break;
      }
      
      parcelas.push({ qntd: parc, valor: valDividido });
      parc++;
    }

    setOpcoesParcelas(parcelas);
  };

  const getDiamantes = () => {
    return Math.floor(valorTotal / diamondsValue);
  };

  const logSaleError = async (context: string, error: unknown) => {
    const errorText = JSON.stringify(error);
    const msg = `---   ❌ ERRO DE VENDA (${context})   ---\n` +
      `Revendedora: ${userData?.codigo || 'N/A'} ${userData?.nome_razao_social || ''}\n` +
      '_______________\n' +
      `- Cliente: ${nomeCliente || 'N/A'}\n` +
      `- CPF Cliente: ${cpfCliente || 'N/A'}\n` +
      `- Valor: R$ ${valorTotal}\n` +
      `- Tipo: ${typeSale}\n` +
      `- Erro: ${errorText}\n`;

    try {
      await telegramLogService.send(true, msg, new Date());
    } catch (sendError) {
      console.error('Falha ao enviar log de erro para telegram:', sendError);
    }
  };

  const validateStep = () => {
    if (valorTotal <= 0) {
      toast({
        title:"Ops!",
        description: "Valor de venda menor ou igual a ZERO.",
        duration: 5000,
      });
      return false;
    }
    
    if (!cpfCliente || cpfCliente.replace(/\D/g, '').length !== 11) {
      toast({
        title: "Ops!",
        description: "Informe um CPF válido do cliente.",
        duration: 5000,
      });
      return false;
    }

    if (!nomeCliente) {
      toast({
        title: "Ops!",
        description: "Informe o nome do cliente.",
        duration: 5000,
      });
      return false;
    }
    
    return true;
  };

  const goToStep = (step: number) => {
    if (!validateStep()) return;
    
    if (step === 1) {
      calculaParcelas();
    }
    
    setCurrentStep(step);
  };

  const goToPix = () => {
    if (!validateStep()) return;
    setTypeSale('pix');
    setCurrentStep(3);
  };

  const goToLink = async () => {
    if (!validateStep()) return;
    
    setTypeSale('link');
    calculaParcelas();
    
    if (!userData) return;

    const parcelas: Parcela[] = [];
    parcelas.push({ qntd: 1, valor: valorTotal });
    
    let parc = 2;
    while (parc <= maxParcelas) {
      let valDividido = valorTotal / parc;
      valDividido = parseFloat(valDividido.toFixed(2));
      
      if (valDividido < valMinParcela) {
        break;
      }
      
      parcelas.push({ qntd: parc, valor: valDividido });
      parc++;
    }
    
    const data = {
      id_revendedora: userData.id,
      codigo_revendedora: userData.codigo,
      nome_revendedora: userData.nome_razao_social,
      numero_revendedora: userData.phones[0].telefone,
      amount: Math.round(valorTotal * 100),
      installments: parcelas.map((parcela: Parcela) => ({
        number: parcela.qntd,
        total: Math.round(valorTotal * 100)
      })),
      nome_cliente: nomeCliente,
      ...(clienteId && { id_cliente: clienteId }), // Incluir ID do cliente se existir
    };

    try {
      showSpinner();
      const response = await saleService.createCheckoutLink(data);
      setLinkCopiaCola(response.checkouts[0].payment_url);
      setCurrentStep(3);
    } catch (error) {
      await Swal.fire({
        title: 'Ops',
        text: 'Não foi possível gerar um link, contate o suporte',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      console.error('Erro na requisição:', error);
    } finally {
      hideSpinner();
    }
  };

  const confirmarVenda = async () => {
    if (!userData) return;

    showSpinner('Aguarde', 'Processando venda...');

    // Validações
    // validade cartao
    if (expCard.length < 5 || expCard[2] !== '/') {
      hideSpinner();
      toast({
        title: 'Ops',
        description: 'Data de VALIDADE do cartão inválida',
        duration: 6000,
      });
      return;
    }
    // valor
    if (valorTotal <= 0) {
      hideSpinner();
      toast({
        title: 'Ops',
        description: 'VALOR da venda inválido',
        duration: 4000,
      });
      return;
    }
    // check card numbers valid
    if (!util.validateCardNumber(numsCard)) {
      hideSpinner();
      toast({
        title: 'Ops',
        description: 'NÚMERO do cartão inválido',
        duration: 4000,
      });
      return;
    }
    // cvv check
    //The field cvv must be a string with a minimum length of 3 and a maximum length of 4.
    if (!(cvvCard && cvvCard.length >= 3 && cvvCard.length <= 4)) {
      hideSpinner();
      toast({
        title: 'Ops',
        description: 'CVV do cartão inválido',
        duration: 3000,
      });
      return;
    }    

    const phone = userData.phones[0].telefone.split(')')[1].replace(/\D/g, '');
    const areaCode = userData.phones[0].telefone.split(')')[0].replace(/\D/g, '');

    const data = {
      id_revendedora: userData.id,
      codigo_revendedora: userData.codigo,
      amount: Math.round(valorTotal * 100),
      nome_revendedora: userData.nome_razao_social,
      email_revendedora: userData.email,
      document_revendedora: userData.cpf_cnpj,
      area_code_revendedora: areaCode,
      phone_revendedora: phone,
      card_number: numsCard.replace(/\D/g, ''),
      card_cvv: cvvCard,
      card_expiration_date: expCard,
      card_holder_name: nameCard,
      installments: parcelaSelecionada.qntd,
      nome_cliente: nomeCliente,
      ...(clienteId && { id_cliente: clienteId }), // Incluir ID do cliente se existir
    };

    try {
      const response = await saleService.createCardSale(data);

      if (response && response.success && response.data.idpagarme) {
        // Sucesso na venda
        let msg = '---   ✔ NOVA VENDA CONFIRMADA NO APP (CARTÃO) 🛍️   ---\n';
        msg += `Revendedora: ${userData.codigo} ${userData.nome_razao_social}\n`;
        msg += '_______________\n';
        msg += '> Dados Venda: \n';
        msg += `  - Cliente: ${nomeCliente}\n`;
        msg += `  - Valor: R$ ${valorTotal} (${parcelaSelecionada.qntd}x)\n`;
        
        if (statusAdesao === 'aprovado') {
          msg += `  - Diamantes: ${getDiamantes()}\n`;
        }
        
        telegramLogService.send(true, msg, new Date());

        hideSpinner();
        await Swal.fire({
          title: 'Sucesso',
          text: 'Venda realizada! Para gerar garantia ou recibo acesse "Minhas Vendas"',
          icon: 'success',
          confirmButtonText: 'OK',
        });

        // Reset form
        setValorTotal(0);
        setNomeCliente('');
        setCpfCliente('');
        setBuscaCliente('');
        setClienteInfo(null);
        setClienteId(null);
        setShowNameInput(false);
        setShowIndicationOption(false);
        setCurrentStep(0);
        setNumsCard('');
        setExpCard('');
        setCvvCard('');
        setNameCard('');

      } else {
        hideSpinner();
        await Swal.fire({
          title: 'Ops',
          text: 'Não foi possível processar a venda. Tente novamente.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      hideSpinner();
      await Swal.fire({
        title: 'Ops',
        text: 'Erro ao processar venda. Contate o suporte.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      console.error('Erro na venda:', error);
      await logSaleError('Venda Cartão', error);
    }
  };

  const finishWithPix = async () => {
    if (!documentPix) {
      await Swal.fire({
        title: 'Ops',
        text: 'Informe o CPF do cliente!',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    if (!userData) return;

    showSpinner('Aguarde', 'Gerando QR Code...');

    const phone = userData.phones[0].telefone.split(')')[1].replace(/\D/g, '');
    const areaCode = userData.phones[0].telefone.split(')')[0].replace(/\D/g, '');

    const data = {
      id_revendedora: userData.id,
      codigo_revendedora: userData.codigo,
      nome_revendedora: userData.nome_razao_social,
      email_revendedora: userData.email,
      phone_revendedora: phone,
      area_code_revendedora: areaCode,
      amount: Math.round(valorTotal * 100),
      nome_cliente: nomeCliente,
      document_cliente: documentPix,
      ...(clienteId && { id_cliente: clienteId }), // Incluir ID do cliente se existir
    };

    try {
      const response = await saleService.createPixSale(data);
      
      if (response.data.qrCodeCopyPaste && response.data.qrCodeLink) {
        setPixCopiaCola(response.data.qrCodeCopyPaste);
        setQrCodePix(response.data.qrCodeLink);
        setSalePixId(response.data.idPagarme);
        setPixExpired(false);
        
        // Iniciar verificação de pagamento
        startPixVerification(response.data.idPagarme);
        
        hideSpinner();
      } else {
        hideSpinner();
        await Swal.fire({
          title: 'Ops',
          text: 'Não foi possível gerar o PIX. Tente novamente.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      hideSpinner();
      await Swal.fire({
        title: 'Ops',
        text: 'Erro ao gerar PIX. Contate o suporte.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      console.error('Erro ao gerar PIX:', error);
      await logSaleError('Venda PIX', error);
    }
  };

  const startPixVerification = (saleId: string) => {
    if(!saleId) return;
    const interval = setInterval(async () => {
      if(!saleId) {
        clearInterval(interval);
        return;
      };
      try {
        const response = await saleService.getSaleDetail(saleId);
        
        if (response.status === 'paid' && response.charges[0].last_transaction.status === 'paid') {
          clearInterval(interval);
          
          // Registrar venda
          await registerPixSale(response);
        }
      } catch (error) {
        console.error('Erro ao verificar pagamento PIX:', error);
        await logSaleError('Verificação PIX', error);
      }
    }, 10000); // Verificar a cada 10 segundos

    // Parar verificação após 15 minutos
    setTimeout(() => {
      clearInterval(interval);
      setPixExpired(true);
    }, 15 * 60 * 1000);
  };

  const registerPixSale = async (saleData: any) => {
    if (!userData) return;

    try {
      const resp = await saleService.registerPixSale(saleData, Number(userData.codigo));
      const respFormated = Array.isArray(resp) ? resp[0] : (resp && resp.data ? resp.data : null);
      
      if (respFormated && respFormated.id) {
        let msg = '---   ✔ NOVA VENDA CONFIRMADA NO APP (PIX) 🛍️   ---\n';
        msg += `Revendedora: ${userData.codigo} ${userData.nome_razao_social}\n`;
        msg += '_______________\n';
        msg += '> Dados Venda: \n';
        msg += `  - Cliente: ${nomeCliente}\n`;
        msg += `  - Valor: R$ ${valorTotal}\n`;
        
        if (statusAdesao === 'aprovado') {
          msg += `  - Diamantes: ${getDiamantes()}\n`;
        }

        telegramLogService.send(true, msg, new Date());

        await Swal.fire({
          title: 'Sucesso',
          text: 'Venda realizada! Para gerar garantia ou recibo acesse "Minhas Vendas"',
          icon: 'success',
          confirmButtonText: 'OK',
        });

        // Reset form
        setValorTotal(0);
        setNomeCliente('');
        setCpfCliente('');
        setBuscaCliente('');
        setClienteInfo(null);
        setClienteId(null);
        setShowNameInput(false);
        setShowIndicationOption(false);
        setCurrentStep(0);
        setDocumentPix('');
        setPixCopiaCola('');
        setQrCodePix('');
      }
    } catch (error) {
      console.error('Erro ao registrar venda PIX:', error);
    }
  };

  const paymentMethods = [
    { id: 'card', label: 'Cartão Créd.', icon: CreditCard, color: 'bg-blue-500', description: 'Parcelamento disponível' },
    { id: 'pix', label: 'VendaPix', icon: Smartphone, color: 'bg-green-500', description: 'Pagamento instantâneo' },
    { id: 'link', label: 'Enviar Link', icon: Link2, color: 'bg-purple-500', description: 'Cliente paga online' }
  ];

  // Componente de Dica
  const TipCard = () => (
    <Alert className="bg-blue-50 border-blue-200">
      <AlertDescription className="text-blue-800">
        <strong>💡 Dica:</strong> Para gerar recibos e garantias de vendas realizadas, acesse o menu <strong>Minhas Vendas!</strong>
      </AlertDescription>
    </Alert>
  );

  // Componente de Progresso
  const ProgressSteps = () => {
    const steps = [
      { id: 0, label: 'Dados', icon: DollarSign },
      { id: 1, label: 'Parcelas', icon: CreditCard },
      { id: 2, label: 'Cartão', icon: CheckCircle },
      { id: 3, label: 'PIX/Link', icon: Smartphone }
    ];

    return (
      <div className="flex items-center justify-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={() => {
          if(currentStep == 3){
            setCurrentStep(0)
          }else{
            setCurrentStep((currentStep >= 1 ? currentStep - 1 : 0))
          }
        }} className="mr-2">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        {steps.map((step, index) => (
          <>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
              currentStep === step.id 
                ? 'bg-primary text-primary-foreground' 
                : currentStep > step.id 
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
            }`}>
              <step.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`w-8 h-0.5 transition-colors ${
                currentStep > step.id ? 'bg-green-300' : 'bg-gray-200'
              }`} />
            )}
          </>
        ))}
      </div>
    );
  }; 

  return (
    <div className="min-h-screen bg-gray-50 pt-6 pb-28">
      <div className="max-w-2xl mx-auto px-4 space-y-6">
        <LoadingSpinner isLoading={isLoading} title={title} message={message} />
        
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Nova Venda</h1>
          {/* <p className="text-gray-600">Registre uma nova venda</p> */}
        </div>

        {/* Progress Steps */}
        <ProgressSteps />
       

        {/* Main Content */}
        <div className="rounded-lg">
          {
            currentStep == 0 && (
              <div className="space-y-6" key="step-0-dados-venda">
                {/* Cabeçalho do Step */}
                {/* <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Dados da Venda</h2>
                        <p className="text-sm text-gray-600">Informe o valor e cliente da venda</p>
                      </div>
                    </div>
                  </CardContent>
                </Card> */}

                {/* Valor da venda */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      Valor da Venda
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-0">
                    <div className="space-y-4">
                      <div className="relative">
                        <div className="flex items-center bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300 focus-within:border-blue-500 transition-colors">
                          <span className="text-3xl font-bold text-gray-600 mr-2">R$</span>
                          <Input
                            id="valor"
                            value={valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            onChange={handleValorChange}
                            className="text-4xl font-bold border-0 px-0 text-right bg-transparent focus:ring-0 focus-visible:ring-0 md:text-4xl"
                            placeholder="0,00"
                          />
                        </div>
                      </div>
                      {/* Informações de diamantes */}
                      {statusAdesao === 'aprovado' && valorTotal >= 50 && (
                        <Card className="shadow-sm border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
                          <CardContent className="py-2 px-2">
                            <div className="text-center">
                              <div className="text-xs font-semibold text-purple-800">
                                💎 Você receberá <span className="text-md font-bold">{getDiamantes()}</span> diamantes no programa de recompensas!
                              </div>
                              {/* <p className="text-sm text-purple-600 mt-1">
                                Programa de Benefícios X do Infinito
                              </p> */}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Busca de cliente */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <User className="w-5 h-5 text-blue-600" />
                      Cliente
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-0">
                    <div className="space-y-4">
                      {/* Campo de busca unificado */}
                      <div className="space-y-2">
                        <Label htmlFor="buscaCliente">Nome ou CPF do Cliente</Label>
                        <div className="relative" ref={dropdownRef}>
                          <div className="flex gap-2">
                            <Input
                              id="buscaCliente"
                              value={buscaCliente}
                              onChange={handleBuscaChange}
                              placeholder="Digite o nome ou CPF"
                              className="text-lg h-12 border-gray-400 flex-1"
                              autoComplete="off"
                            />
                            <Button
                              type="button"
                              onClick={() => searchClient()}
                              disabled={isSearchingClient || cpfCliente.replace(/\D/g, '').length !== 11}
                              className="h-12 px-4"
                            >
                              {isSearchingClient ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Search className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          {/* Dropdown de autocomplete */}
                          {(() => {
                            const dropdownItems = dropdownAberto ? getDropdownFiltrado() : [];
                            return dropdownItems.length > 0 ? (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                {dropdownItems.map((cliente) => (
                                  <button
                                    key={cliente.id}
                                    type="button"
                                    className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      selecionarClienteDropdown(cliente);
                                    }}
                                  >
                                    <div className="font-medium text-gray-900">{cliente.nome}</div>
                                    <div className="text-sm text-gray-500 flex gap-3">
                                      <span>{cliente.cpf}</span>
                                      {cliente.telefone && <span>{cliente.telefone}</span>}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      {/* Resultado da busca */}
                      {clienteInfo && !clienteInfo.isSelf && (
                        <div className="mt-4 space-y-3">
                          {/* Informações do cliente encontrado */}
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <UserCheck className="w-4 h-4 text-blue-600" />
                              <span className="font-medium text-blue-800">Cliente encontrado</span>
                            </div>
                            <div className="text-sm text-blue-700">
                              <div><strong>Nome:</strong> {clienteInfo.nome}</div>
                              <div><strong>CPF:</strong> {clienteInfo.cpf}</div>
                              {clienteInfo.telefone && (
                                <div><strong>Telefone:</strong> {clienteInfo.telefone}</div>
                              )}                         
                            </div>
                          </div>
                          {/* Cliente indicado pela revendedora atual */}
                          {clienteInfo.isIndicatedByCurrentUser && (
                            <Alert className="bg-green-50 border-green-200">                        
                              <AlertDescription className="text-green-800">
                                {getDiamantes() > 0 ? (
                                  <>
                                  🎉 Cliente acumula <strong>{getDiamantes()}</strong> diamantes por essa compra.
                                  Você também acumula <strong>mais diamantes</strong> pela indicação na rede!
                                  </>
                                ) : (
                                  <>
                                  Você acumula <strong>mais diamantes</strong> pela indicação na rede!
                                  </>
                                )}
                              
                              </AlertDescription>
                            </Alert>
                          )}
                          {/* Cliente está na rede nos níveis 2 ou 3 */}
                          {clienteInfo.isIndicatedInLevel2or3 && (
                            <Alert className="bg-purple-50 border-purple-200">                        
                              <AlertDescription className="text-purple-800">
                              {getDiamantes() > 0 ? (
                                <>
                                  💎 Cliente acumula <strong>{getDiamantes()}</strong> diamantes por essa compra.
                                  Você acumula <strong>mais diamantes</strong> pela venda na sua rede (nível {clienteInfo.rede?.nivel_na_rede})!
                                </>
                              ):
                              (
                                <>
                                  Você acumula <strong>mais diamantes</strong> pela venda na sua rede (nível {clienteInfo.rede?.nivel_na_rede})!
                                </>
                              )}
                              </AlertDescription>
                            </Alert>
                          )}
                          {/* Cliente será adicionado à rede */}
                          {clienteInfo.willBeAdded && (
                            <Alert className="bg-blue-50 border-blue-200">
                              <Info className="w-4 h-4 text-blue-600" />
                              <AlertDescription className="text-blue-800">
                                🎉 <strong>Nova cliente na sua rede!</strong> Este cliente será adicionado à sua rede de indicações. 
                                Você acumulará <strong>15 diamantes</strong> por adicionar ela à sua rede, e ela também acumula <strong>{getDiamantes()}</strong> diamantes por essa compra!
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}

                      {/* Campo nome (quando necessário) */}
                      {showNameInput && (
                        <div className="space-y-2">
                          <Label htmlFor="nomeCliente">Nome do Cliente</Label>
                          <Input
                            id="nomeCliente"
                            value={nomeCliente}
                            onChange={(e) => setNomeCliente(e.target.value)}
                            placeholder="Digite o nome completo"
                            className="text-lg h-12 border-gray-400"
                          />
                        </div>
                      )}

                      {clienteInfo && clienteInfo.isSelf && (
                        <div className="mt-4 space-y-3">
                          {/* Cliente é a própria revendedora */}                      
                          <Alert className="bg-orange-50 border-orange-200">
                            <AlertTriangle className="w-4 h-4 text-orange-600" />
                            <AlertDescription className="text-orange-800">
                              <strong>Atenção:</strong> Você está efetuando uma venda para si mesma e não acumula diamantes no <strong>programa de indicações</strong>.
                            </AlertDescription>
                          </Alert>
                        </div>
                      )}

                      {/* Opção de indicação para cliente não encontrado */}
                      {showIndicationOption && !clienteInfo && (
                        <div className="mt-4">
                          <Alert className="bg-blue-50 border-blue-200">                        
                            <AlertDescription className="text-blue-800">
                              <div className="space-y-3">
                                <div className='text-xs'>
                                  <strong>Cliente não encontrado.</strong>
                                  <br />
                                  Antes de continuar, você pode compartilhar seu <strong>link de indicação</strong> para que esta pessoa faça parte da sua rede, gerando mais diamantes tanto para você quanto para ela.
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={copyIndicationLink}
                                    className="flex items-center gap-2"
                                  >
                                    <Copy className="w-4 h-4" />
                                    Copiar Link
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={shareIndicationLink}
                                    className="flex items-center gap-2"
                                  >
                                    <Share2 className="w-4 h-4" />
                                    Compartilhar
                                  </Button>
                                </div>
                                <div className="text-xs text-blue-600">
                                  💡 <strong>Lembre-se:</strong> Após a cliente se cadastrar pelo seu link, faça uma nova busca pelo CPF.
                                </div>
                              </div>
                            </AlertDescription>
                          </Alert>
                        </div>
                      )}

                    </div>
                  </CardContent>
                </Card>
                

                {/* Método de pagamento */}
                <Card className="shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Escolha o Método de Pagamento</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-0">
                    <div className="grid gap-4">
                      <Button
                        variant="outline"
                        className="h-16 flex items-center justify-between p-4 hover:bg-blue-50 hover:border-blue-300 transition-all group"
                        onClick={() => goToStep(1)}                     
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                            <CreditCard className="w-5 h-5 text-white" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold">Cartão de Crédito</div>
                            <div className="text-sm text-gray-500">Parcelamento disponível</div>
                          </div>
                        </div>
                      </Button>
                      
                      <Button
                        variant="outline"
                        className="h-16 flex items-center justify-between p-4 hover:bg-green-50 hover:border-green-300 transition-all group"
                        onClick={goToPix}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center group-hover:bg-green-600 transition-colors">
                            <QrCode className="w-5 h-5 text-white" />
                          </div>
                          <div className="text-left">
                            <div className="font-semibold">Pix</div>
                            <div className="text-sm text-gray-500">Pagamento instantâneo</div>
                          </div>
                        </div>
                      </Button>
                      
                      {!linkPgtoAtivoAdmin && (
                        <Button
                          variant="outline"
                          className="h-16 flex items-center justify-between p-4 hover:bg-purple-50 hover:border-purple-300 transition-all group"
                          onClick={goToLink}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center group-hover:bg-purple-600 transition-colors">
                              <Link2 className="w-5 h-5 text-white" />
                            </div>
                            <div className="text-left">
                              <div className="font-semibold">Enviar Link</div>
                              <div className="text-sm text-gray-500">Cliente paga online</div>
                            </div>
                          </div>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )
          }
          {
            currentStep == 1 && (
              <div className="space-y-6" key="step-1-parcelamento">
                {/* Cabeçalho do Step */}
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Button variant="ghost" size="sm" onClick={() => setCurrentStep(0)} className="mr-2">
                        <ArrowLeft className="w-4 h-4" />
                      </Button>
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Parcelamento</h2>
                        <p className="text-sm text-gray-600">Escolha a forma de pagamento</p>
                      </div>
                    </div>
                    
                    {/* Resumo da venda */}
                    <div className="bg-white/70 rounded-lg p-4 mt-4">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Cliente:</span>
                        <span className="font-medium">{nomeCliente}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-gray-600">Valor Total:</span>
                        <span className="font-bold text-lg text-green-700">{util.formatCurrency(valorTotal)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Opções de parcelamento */}
                <Card className="shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Opções de Parcelamento</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-0">
                    <div className="space-y-3">
                      {opcoesParcelas.map((parcela, index) => (
                        <Button
                          key={parcela.qntd}
                          variant="outline"
                          className={`w-full justify-between h-16 p-4 hover:bg-green-50 hover:border-green-300 transition-all group`}
                          onClick={() => {
                            setParcelaSelecionada(parcela);
                            setCurrentStep(2);
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold bg-blue-500 group-hover:bg-green-500`}>
                              {parcela.qntd}x
                            </div>
                            <div className="text-left">
                              <div className="font-semibold text-lg">
                                {parcela.qntd}x de {util.formatCurrency(parcela.valor)}
                              </div>
                              {index === 0 && (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                  À vista
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-600">Total</div>
                            <div className="font-bold text-green-700">
                              {util.formatCurrency(valorTotal)}
                            </div>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )
          }
          {
            currentStep == 2 && (
              <div className="space-y-6">
                {/* Cabeçalho do Step */}
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">                 
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Dados da Venda</h2>
                      </div>
                    </div>
                    
                    {/* Resumo da venda */}
                    <div className="bg-white/70 rounded-lg p-4 mt-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Cliente:</span>
                          <div className="font-medium">{nomeCliente}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Parcelamento:</span>
                          <div className="font-medium">{parcelaSelecionada.qntd}x de {util.formatCurrency(parcelaSelecionada.valor)}</div>
                        </div>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total:</span>
                        <span className="font-bold text-lg text-green-900">{util.formatCurrency(valorTotal)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Formulário do cartão */}
                <Card className="shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Informações do Cartão</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-0">
                    <div className="space-y-6">
                      {/* Número do cartão */}
                      <div className="space-y-2">
                        <Label htmlFor="cardNumber" className="text-sm font-medium">Número do Cartão</Label>
                        <div className="relative">
                          <Input
                            id="cardNumber"
                            value={numsCard}
                            onChange={(e) => setNumsCard(util.formatCardNumber(e.target.value))}
                            placeholder="0000 0000 0000 0000"
                            maxLength={19}
                            className="pl-12 h-12 text-lg border-gray-400"
                          />
                          <CreditCard className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Validade e CVV */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="expiry" className="text-sm font-medium">Validade</Label>
                          <div className="relative">
                            <Input
                              id="expiry"
                              value={expCard}
                              onChange={(e) => setExpCard(util.formatCardExpiry(e.target.value))}
                              placeholder="MM/AA"
                              maxLength={5}
                              className="h-12 text-lg text-center border-gray-400"
                            />
                            <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="cvv" className="text-sm font-medium">CVV</Label>
                          <div className="relative">
                            <Input
                              id="cvv"
                              value={cvvCard}
                              onChange={(e) => setCvvCard(e.target.value.replace(/\D/g, ''))}
                              placeholder="000"
                              maxLength={4}
                              className="h-12 text-lg text-center border-gray-400"
                            />
                            <Shield className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Nome no cartão */}
                      <div className="space-y-2">
                        <Label htmlFor="cardName" className="text-sm font-medium">Nome no Cartão</Label>
                        <Input
                          id="cardName"
                          value={nameCard}
                          onChange={(e) => {
                            // remove acentos
                            setNameCard(util.removeAccents(e.target.value));
                          }}
                          placeholder="Ex.: Beltrano P Silva"
                          className="h-12 text-lg border-gray-400"
                        />
                        <p className="text-sm text-gray-600">Nome do titular escrito no cartão, sem acentos</p>
                      </div>
                      
                      {/* Botão de confirmação */}
                      <Button 
                        className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                        onClick={confirmarVenda}
                        disabled={!numsCard || !expCard || !cvvCard || !nameCard}
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Confirmar Venda - {util.formatCurrency(valorTotal)}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>        
            )
          }
          {
            (currentStep == 3 && typeSale === 'pix') && (
              <div className="space-y-6" key="step-3-pix">
                {/* Cabeçalho do Step */}
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Button variant="ghost" size="sm" onClick={() => setCurrentStep(0)} className="mr-2">
                        <ArrowLeft className="w-4 h-4" />
                      </Button>
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Pagamento PIX</h2>
                        <p className="text-sm text-gray-600">Gere o código PIX para o cliente</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {!pixCopiaCola ? (
                  /* Formulário PIX */
                  <Card className="shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg">Dados da Venda</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 pt-0">
                      <div className="space-y-6">
                        {/* Resumo da venda */}
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Cliente:</span>
                              <div className="font-medium">{nomeCliente}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Valor:</span>
                              <div className="font-bold text-lg text-green-700">{util.formatCurrency(valorTotal)}</div>
                            </div>
                          </div>
                        </div>

                        {/* CPF/CNPJ do cliente */}
                        <div className="space-y-2">
                          <Label htmlFor="documentPix" className="text-sm font-medium">CPF do Cliente</Label>
                          <Input
                            id="documentPix"
                            value={documentPix}
                            onChange={(e) => setDocumentPix(util.formatCPFCNPJ(e.target.value))}
                            placeholder="Digite aqui o CPF"
                            className="h-12 text-lg border-gray-400"
                          />
                        </div>
                        
                        <Button 
                          className="w-full h-12 text-lg font-semibold bg-green-500 hover:bg-green-600"
                          onClick={finishWithPix}
                          disabled={!documentPix}
                        >
                          <Smartphone className="w-5 h-5 mr-2" />
                          Gerar PIX
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  /* PIX Gerado */
                  <div className="space-y-6">
                    <Card className="shadow-sm border-green-200">
                      <CardHeader className="bg-green-50">
                        <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          PIX Gerado com Sucesso!
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6">
                        {/* Informações da venda */}
                        <div className="grid grid-cols-3 gap-4 mb-6 text-center">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-2xl font-bold text-green-600">{util.formatCurrency(valorTotal)}</div>
                            <div className="text-xs text-gray-600">Valor</div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-sm font-medium">{nomeCliente}</div>
                            <div className="text-xs text-gray-600">Cliente</div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-sm font-medium flex items-center justify-center gap-1">
                              <Clock className="w-4 h-4" />
                              15min
                            </div>
                            <div className="text-xs text-gray-600">Validade</div>
                          </div>
                        </div>

                        {/* QR Code */}
                        {qrCodePix && (
                          <div className="text-center mb-6">
                            <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 inline-block">
                              <img 
                                src={qrCodePix} 
                                alt="QR Code PIX" 
                                className="w-48 h-48"
                              />
                            </div>
                            <p className="text-sm text-gray-600 mt-2">Escaneie o QR Code com o app do banco</p>
                          </div>
                        )}
                        
                        {/* PIX Copia e Cola */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">PIX Copia e Cola:</Label>
                          <div className="flex gap-2">
                            <Input 
                              value={pixCopiaCola} 
                              readOnly 
                              className="text-xs font-mono"
                            />
                            <Button 
                              onClick={() => {
                                navigator.clipboard.writeText(pixCopiaCola);
                                Swal.fire('Copiado!', 'PIX copiado para a área de transferência', 'success');
                              }}
                              className="shrink-0"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {pixExpired && (
                          <Alert className="mt-4 border-red-200 bg-red-50">
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                            <AlertDescription className="text-red-800">
                              <strong>PIX Expirado!</strong> Gere um novo PIX para continuar.
                            </AlertDescription>
                          </Alert>
                        )}

                        <div className="flex gap-2 mt-6">
                          <Button 
                            className="flex-1"
                            variant="outline"
                            onClick={() => {
                              setPixCopiaCola('');
                              setQrCodePix('');
                              setPixExpired(false);
                              setDocumentPix('');
                            }}
                          >
                            Gerar Novo PIX
                          </Button>
                          <Button 
                            className="flex-1"
                            onClick={() => {
                              window.open(`https://api.whatsapp.com/send?text=${pixCopiaCola}`, '_blank');
                            }}
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            WhatsApp
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            )
          }
          {
            (currentStep == 3 && typeSale === 'link') && (
              <div className="space-y-6" key="step-3-link">
                {/* Cabeçalho do Step */}
                <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Button variant="ghost" size="sm" onClick={() => setCurrentStep(0)} className="mr-2">
                        <ArrowLeft className="w-4 h-4" />
                      </Button>
                      <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                        <Link2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Link de Pagamento</h2>
                        <p className="text-sm text-gray-600">Compartilhe o link com o cliente</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm border-purple-200">
                  <CardHeader className="bg-purple-50">
                    <CardTitle className="text-lg text-purple-800 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Link Gerado com Sucesso!
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Informações da venda */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-purple-600">{util.formatCurrency(valorTotal)}</div>
                        <div className="text-xs text-gray-600">Valor da Venda</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-lg font-medium">{nomeCliente}</div>
                        <div className="text-xs text-gray-600">Cliente</div>
                      </div>
                    </div>

                    {/* Link para compartilhar */}
                    <div className="space-y-2 mb-6">
                      <Label className="text-sm font-medium">Link para compartilhar:</Label>
                      <div className="flex gap-2">
                        <Input value={linkCopiaCola} readOnly className="font-mono text-sm" />
                        <Button 
                          onClick={() => {
                            navigator.clipboard.writeText(linkCopiaCola);
                            Swal.fire('Copiado!', 'Link copiado para a área de transferência', 'success');
                          }}
                          className="shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(linkCopiaCola);
                          Swal.fire('Copiado!', 'Link copiado para a área de transferência', 'success');
                        }}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copiar Link
                      </Button>
                      <Button 
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => {
                          window.open(`https://api.whatsapp.com/send?text=Olá, segue o link para pagamento da sua compra X do Infinito no valor de ${util.formatCurrency(valorTotal)}: ${linkCopiaCola}`, '_blank');
                        }}
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )
          }
        </div>

        {/* Tip Card - Sempre visível */}
        <TipCard />

        {/* Modal de Completar Cadastro */}
        <CompletarCadastro
          isOpen={showCompletarCadastro}
          userData={userData}
          onSuccess={handleCompletarCadastroSuccess}
        />
      </div>
    </div>
  );
};

export default NovaVenda;